/**
 * Documents section for specialist / consultant profiles (detail & edit forms)
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  useCreateProviderDocumentMutation,
  useDeleteProviderDocumentMutation,
  useGetProviderDocumentsQuery,
  useUpdateProviderDocumentMutation,
} from '../../store/api/providerDocumentsApi';
import type { ProviderDocument } from '../../types/providerDocument';
import ProviderDocumentDialog from './ProviderDocumentDialog';

type ProviderKind = 'specialists' | 'consultants';

interface ProviderDocumentsSectionProps {
  kind: ProviderKind;
  providerId: number;
  canManage: boolean;
  embedded?: boolean;
}

const ProviderDocumentsSection = ({
  kind,
  providerId,
  canManage,
  embedded = false,
}: ProviderDocumentsSectionProps) => {
  const { data, isLoading, refetch } = useGetProviderDocumentsQuery({ kind, providerId });
  const [createDocument, { isLoading: isCreating }] = useCreateProviderDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateProviderDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteProviderDocumentMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderDocument | null>(null);

  const documents = data?.data || [];

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (document: ProviderDocument) => {
    setEditing(document);
    setDialogOpen(true);
  };

  const handleSave = async ({ name, file }: { name: string; file: File | null }) => {
    if (!name.trim()) {
      toast.error('Document name is required');
      return;
    }
    if (!editing && !file) {
      toast.error('Please choose a file');
      return;
    }

    const formData = new FormData();
    formData.append('name', name.trim());
    if (file) {
      formData.append('file', file);
    }

    try {
      if (editing) {
        await updateDocument({
          kind,
          providerId,
          documentId: editing.id,
          formData,
        }).unwrap();
        toast.success('Document updated');
      } else {
        await createDocument({ kind, providerId, formData }).unwrap();
        toast.success('Document added');
      }
      setDialogOpen(false);
      refetch();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'Failed to save document');
    }
  };

  const handleDelete = async (documentId: number) => {
    try {
      await deleteDocument({ kind, providerId, documentId }).unwrap();
      toast.success('Document deleted');
      refetch();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'Failed to delete document');
    }
  };

  const content = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <DescriptionIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" lineHeight={1.2}>
              Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Licenses, certificates, and other provider files
            </Typography>
          </Box>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add document
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : documents.length === 0 ? (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <DescriptionIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No documents uploaded yet</Typography>
          {canManage && (
            <Button size="small" sx={{ mt: 1 }} onClick={openCreate}>
              Add the first document
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Added</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {document.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={document.file_url || document.file}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                    >
                      View file
                    </Link>
                  </TableCell>
                  <TableCell>{new Date(document.created_at).toLocaleDateString()}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(document)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(document.id)}
                        disabled={isDeleting}
                        aria-label="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ProviderDocumentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
        editing={Boolean(editing)}
        initialName={editing?.name || ''}
      />
    </>
  );

  if (embedded) {
    return <Box sx={{ mt: 3 }}>{content}</Box>;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default ProviderDocumentsSection;
